import maplibregl from "maplibre-gl";
import type { DrawAOIFeature } from "../types";

/**
 * The area note pinned beside each drawn AOI.
 *
 * Every polygon gets one, not only the ones that break a rule: the number in
 * the side panel describes a single AOI, so with more than one on the map there
 * is nothing tying a figure to a shape. Out-of-range areas take the same slot in
 * red, which keeps the rejection where the user is already looking for the size.
 *
 * Rendered as DOM markers rather than a symbol layer: a symbol layer can only
 * draw text through the style's glyph server, and the basemaps this app ships
 * point at one that lacks the default font stack — the layer then fails
 * silently, which is the worst possible outcome for a warning. Markers also let
 * the text wrap and carry a background, so it stays readable over imagery.
 */

export interface AreaLabel {
  featureId: string;
  /** Anchor point, [lng, lat]. */
  at: [number, number];
  text: string;
  /** Outside the provider's accepted range. */
  invalid: boolean;
}

/** Northernmost vertex of the ring, so the note sits above the shape. */
export const topOfPolygon = (
  feature: DrawAOIFeature,
): [number, number] | null => {
  if (feature.geometry?.type !== "Polygon") return null;

  const ring = feature.geometry.coordinates?.[0];
  if (!ring?.length) return null;

  let top = ring[0];
  ring.forEach((point) => {
    if (point[1] > top[1]) top = point;
  });

  return [top[0], top[1]];
};

/**
 * Build the marker's element as a bare wrapper with the styled caption INSIDE it.
 *
 * MapLibre writes the marker's screen position into `element.style.transform`.
 * Styling that same element with `style.cssText = ...` replaces the whole inline
 * style block and takes the transform with it — the label then sits at the
 * container's top-left until the next map move re-runs `Marker._update()`, which
 * is exactly the "it only snaps into place when I zoom" symptom. Keeping our
 * styling one level down means nothing here can ever clobber it.
 */
const createElement = (): HTMLElement => {
  const wrapper = document.createElement("div");
  wrapper.appendChild(document.createElement("div"));
  return wrapper;
};

const applyLabel = (wrapper: HTMLElement, label: AreaLabel): HTMLElement => {
  const root = wrapper.firstElementChild as HTMLElement;
  root.textContent = label.text;
  root.style.cssText = [
    "pointer-events:none",
    "max-width:220px",
    "padding:4px 8px",
    "border-radius:6px",
    "font-size:11px",
    "font-weight:600",
    "line-height:1.35",
    "text-align:center",
    "box-shadow:0 2px 10px rgba(0,0,0,0.45)",
    label.invalid
      ? "border:1px solid rgba(248,113,113,0.55);background:rgba(40,8,12,0.92);color:#fca5a5"
      : "border:1px solid rgba(255,255,255,0.18);background:rgba(10,14,26,0.9);color:#e2e8f0",
  ].join(";");
  return wrapper;
};

export class AreaLabelMarkers {
  private map: maplibregl.Map;
  private markers = new Map<string, maplibregl.Marker>();

  constructor(map: maplibregl.Map) {
    this.map = map;
  }

  setData(labels: AreaLabel[]): void {
    const seen = new Set<string>();

    labels.forEach((label) => {
      seen.add(label.featureId);
      const existing = this.markers.get(label.featureId);

      if (existing) {
        existing.setLngLat(label.at);
        applyLabel(existing.getElement(), label);
        return;
      }

      const marker = new maplibregl.Marker({
        element: applyLabel(createElement(), label),
        anchor: "bottom",
        offset: [0, -8],
        // Hidden rather than ghosted when the globe turns the shape away.
        opacityWhenCovered: "0",
      })
        .setLngLat(label.at)
        .addTo(this.map);

      this.markers.set(label.featureId, marker);
    });

    this.markers.forEach((marker, id) => {
      if (seen.has(id)) return;
      marker.remove();
      this.markers.delete(id);
    });
  }

  destroy(): void {
    this.markers.forEach((marker) => marker.remove());
    this.markers.clear();
  }
}
